import { Component, createSignal, Show } from "solid-js";
// Note: Using native <a> tag instead of router <A> because this component
// is used in ErrorBoundary which may render outside Router context

// ============================================================================
// Error Fallback Component
// ============================================================================
// Provides a user-friendly error display with recovery options.
// Used as the fallback for ErrorBoundary wrappers throughout the app.

export interface ErrorFallbackProps {
  error: Error;
  reset: () => void;
}

/**
 * ErrorFallback - Displays a friendly error message with recovery options.
 * 
 * Features:
 * - Shows error message (expandable details in dev mode)
 * - "Try Again" button to reset the error boundary
 * - "Go Home" link for navigation escape hatch
 * - Respects reduced motion preferences
 */
const ErrorFallback: Component<ErrorFallbackProps> = (props) => {
  const [showDetails, setShowDetails] = createSignal(false);

  // Check if we're in development mode
  const isDev = import.meta.env.DEV;

  return (
    <div class="flex min-h-[400px] flex-col items-center justify-center px-4 py-8">
      <div class="max-w-md text-center">
        {/* Error Icon */}
        <div class="mb-4 text-6xl">⚠️</div>

        {/* Error Title */}
        <h1 class="mb-2 text-2xl font-bold text-neutral-100">
          Something went wrong
        </h1>

        {/* Error Description */}
        <p class="mb-6 text-neutral-400">
          We encountered an unexpected error. Don't worry, your data is safe.
        </p>

        {/* Error Message (always shown) */}
        <div class="mb-6 rounded-lg bg-neutral-800/50 p-4 text-left">
          <p class="font-mono text-sm text-red-400">
            {props.error.message || "An unknown error occurred"}
          </p>
        </div>

        {/* Stack Trace (dev only, expandable) */}
        <Show when={isDev && props.error.stack}>
          <div class="mb-6">
            <button
              type="button"
              onClick={() => setShowDetails((prev) => !prev)}
              class="text-sm text-neutral-500 underline hover:text-neutral-300"
            >
              {showDetails() ? "Hide" : "Show"} technical details
            </button>

            <Show when={showDetails()}>
              <pre class="mt-2 max-h-48 overflow-auto rounded-lg bg-neutral-900 p-3 text-left font-mono text-xs text-neutral-500">
                {props.error.stack}
              </pre>
            </Show>
          </div>
        </Show>

        {/* Recovery Actions */}
        <div class="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={() => props.reset()}
            class="rounded-lg bg-accent-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 focus:ring-offset-neutral-900"
          >
            Try Again
          </button>

          <a
            href="/home"
            class="rounded-lg border border-neutral-700 px-6 py-2.5 font-medium text-neutral-300 transition-colors hover:border-neutral-500 hover:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 focus:ring-offset-neutral-900"
          >
            Go Home
          </a>
        </div>

        {/* Help Text */}
        <p class="mt-6 text-xs text-neutral-500">
          If this keeps happening, try refreshing the page or clearing your browser cache.
        </p>
      </div>
    </div>
  );
};

export default ErrorFallback;
